<template>
  <div class="training-calendar board-calendar">
    <b-card no-body>
      <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
        <h4 class="mb-0">Board Calendar</h4>
        <div class="d-flex align-items-center flex-wrap">
          <span class="legend mr-1"><span class="dot dot-scheduled"></span>Scheduled</span>
          <span class="legend mr-1"><span class="dot dot-completed"></span>Completed</span>
          <span class="legend mr-1"><span class="dot dot-cancelled"></span>Cancelled</span>
          <b-button size="sm" variant="outline-primary" class="mr-50" @click="goToday">Today</b-button>
          <b-button
            v-if="$Can('create_governance_event')"
            size="sm"
            variant="primary"
            :to="{ name: 'governance-event-add' }"
          >
            <feather-icon icon="PlusIcon" class="mr-25" />Add Event
          </b-button>
        </div>
      </b-card-header>
      <b-card-body>
        <b-overlay :show="loading" rounded="sm" no-fade>
          <FullCalendar ref="fc" :options="calendarOptions" />
        </b-overlay>
      </b-card-body>
    </b-card>

    <governance-event-details-modal
      modal-id="board-calendar-details"
      :event-id="selectedEventId"
      @responded="reload"
      @hidden="selectedEventId = null"
    />
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import moment from "moment";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import governanceMixin, { registerGovernanceStore, unregisterGovernanceStore } from "./governanceMixin";
import GovernanceEventDetailsModal from "./GovernanceEventDetailsModal.vue";

export default {
  name: "BoardCalendar",
  components: { FullCalendar, GovernanceEventDetailsModal },
  mixins: [governanceMixin],
  setup() {
    registerGovernanceStore();
    onUnmounted(() => unregisterGovernanceStore());
  },
  data() {
    return {
      loading: false,
      selectedEventId: null,
      lastRange: { start: null, end: null },
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
        initialView: "dayGridMonth",
        headerToolbar: {
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay,listMonth",
        },
        firstDay: 1,
        events: [],
        eventClick: this.onEventClick,
        eventClassNames: this.eventClassNames,
        height: "auto",
        dayMaxEvents: 3,
        nowIndicator: true,
        selectable: false,
        datesSet: this.onDatesSet,
      },
    };
  },
  methods: {
    onDatesSet(arg) {
      const start = moment(arg.start).format("YYYY-MM-DD");
      const end = moment(arg.end).format("YYYY-MM-DD");
      if (this.lastRange.start === start && this.lastRange.end === end) return;
      this.lastRange = { start, end };
      this.load(start, end);
    },
    reload() {
      if (this.lastRange.start) this.load(this.lastRange.start, this.lastRange.end);
    },
    load(start, end) {
      this.loading = true;
      store
        .dispatch("governance/fetchCalendar", { start, end })
        .then((res) => this.setCalendarEvents(res.data || []))
        .catch((error) => this.govToast("Could not load calendar", "danger", this.govErrorMessage(error)))
        .finally(() => {
          this.loading = false;
        });
    },
    setCalendarEvents(events) {
      const ref = this.$refs.fc;
      if (ref && typeof ref.getApi === "function") {
        const api = ref.getApi();
        api.removeAllEvents();
        events.forEach((ev) => api.addEvent(ev));
      } else {
        this.calendarOptions.events = events;
      }
    },
    eventClassNames(arg) {
      const status = arg.event.extendedProps.status || "scheduled";
      return [`gov-event-${status}`];
    },
    onEventClick(info) {
      this.selectedEventId = info.event.extendedProps.event_id || info.event.id;
      this.$nextTick(() => this.$bvModal.show("board-calendar-details"));
    },
    goToday() {
      const ref = this.$refs.fc;
      if (ref && typeof ref.getApi === "function") ref.getApi().today();
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/apps/calendar.scss";

.board-calendar {
  .legend {
    font-size: 0.85rem;
    display: inline-flex;
    align-items: center;
  }
  .dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 4px;
  }
  .dot-scheduled { background: #7367f0; }
  .dot-completed { background: #28c76f; }
  .dot-cancelled { background: #ea5455; }

  .fc-event.gov-event-scheduled { background: rgba(115, 103, 240, 0.15); border-color: #7367f0; color: #5e50ee; }
  .fc-event.gov-event-completed { background: rgba(40, 199, 111, 0.15); border-color: #28c76f; color: #1f9d57; }
  .fc-event.gov-event-cancelled { background: rgba(234, 84, 85, 0.15); border-color: #ea5455; color: #ea5455; text-decoration: line-through; }
  .fc-event { cursor: pointer; }
}
</style>
